import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg';
import { ReactComponent as LockIcon } from 'assets/icons/lock.svg';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import { ReasonFilters } from 'components/Pages/CancelationReasons';
import {
  FormPageHeader,
  Input,
  ListingPageHeader,
  Paginator,
  Select,
  SettingsMenu,
  SortableHeader,
  SortableHeadersGroup,
  ToggleInput,
} from 'components/Shared';
import { DefaultSelectStyle } from 'components/Shared/Forms/Select/styles';
import { Button } from 'components/ui/button';
import type { SelectOption } from 'contracts/Common';
import type {
  ViagensJustificationOption,
  ViagensJustificationType,
} from 'contracts/ViagensJustificationOptions';
import { Scaffold } from 'layouts';
import { cn } from 'lib/utils';
import { ArrowLeft, TruckIcon } from 'lucide-react';
import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, handleApiError, notify } from 'services';

const endpoint = 'viagens-controle/justifications-options/manage';
const typeOptions: SelectOption[] = [
  { value: 'estadia', label: 'Estadia' },
  { value: 'fatura', label: 'Fatura' },
  { value: 'estadia-estorno', label: 'Fatura-Estorno' },
];
const typeLabel = (type: ViagensJustificationType) =>
  typeOptions.find((option) => option.value === type)?.label || type;
const isBlocked = (item: ViagensJustificationOption) =>
  Boolean(item.deleted_at || item.deletedAt);

const pageContainerClass = cn('flex flex-col gap-6');
const listPanelClass = cn('flex w-full flex-col bg-white p-8');
const formPanelClass = cn('flex w-full flex-col bg-white p-6');
const selectStyles = {
  ...DefaultSelectStyle,
  control: (provided: any, state: any) => ({
    ...DefaultSelectStyle.control?.(provided, state),
    height: '48px',
    minHeight: '48px',
  }),
};

export const ViagensJustificationsListPage: React.FC = () => {
  const [items, setItems] = React.useState<ViagensJustificationOption[]>([]);
  const [search, setSearch] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [sort, setSort] = React.useState({
    orderBy: 'id',
    direction: 'desc' as 'asc' | 'desc',
  });
  const [pagination, setPagination] = React.useState<any>({
    currentPage: 1,
    lastPage: 1,
    total: 0,
  });
  const [loading, setLoading] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint, {
        params: {
          search,
          page,
          limit: 10,
          orderBy: sort.orderBy,
          direction: sort.direction,
        },
      });
      const response = data?.data ? data : { data };
      setItems(response.data || []);
      setPagination(
        response.meta || {
          currentPage: page,
          lastPage: 1,
          total: response.data?.length || 0,
        }
      );
    } catch (error) {
      notify('error', handleApiError(error).message);
    } finally {
      setLoading(false);
    }
  }, [page, search, sort]);

  const handleSort = React.useCallback(
    (nextSort: { orderBy?: string; direction?: 'asc' | 'desc' }) => {
      setSort({
        orderBy: nextSort.orderBy || 'id',
        direction: nextSort.direction || 'asc',
      });
      setPage(1);
    },
    []
  );

  React.useEffect(() => {
    load();
  }, [load]);

  return (
    <Scaffold>
      <SettingsMenu />
      <main className={cn(pageContainerClass)}>
        <section className={cn(listPanelClass, 'rounded-lg')}>
          <ListingPageHeader
            icon={<TruckIcon className={cn('h-6 w-6 text-slate-700')} />}
            title="Justificativas de Estadia"
            isLoading={loading}
            actions={
              <Button
                asChild
                size="sm"
                className={cn(
                  '!h-8 !rounded-md !bg-[#EE7C2C] !px-4 !py-0',
                  'gap-2 text-sm font-GilroyBold text-white hover:!bg-[#d96d24]'
                )}
              >
                <Link to="/configuracoes/justificativas-estadia/criar">
                  <PlusIcon
                    className={cn(
                      'h-[22px] w-[22px] text-white [&_path]:fill-current'
                    )}
                  />
                  Nova justificativa
                </Link>
              </Button>
            }
          />
          <ReasonFilters
            currentFilter={{ search }}
            onFilter={(filter) => {
              setPage(1);
              setSearch(filter.search || '');
            }}
          />
          <div className={cn('overflow-x-auto')}>
            <div className={cn('min-w-[760px]')}>
              <div
                className={cn(
                  'mb-2 grid grid-cols-[60px_minmax(240px,420px)_180px_1fr_32px_32px] items-center gap-x-4 border-b border-slate-200 px-0 py-4 text-[13px] font-GilroySemiBold'
                )}
              >
                <SortableHeadersGroup currentSort={sort} onSort={handleSort}>
                  <SortableHeader column="id" label="ID" />
                  <SortableHeader column="description" label="NOME" />
                  <SortableHeader column="type" label="TIPO" />
                  <div />
                  <div />
                  <div />
                </SortableHeadersGroup>
              </div>
              {items.map((item) => {
                const blocked = isBlocked(item);
                return (
                  <div
                    key={item.id}
                    className={cn(
                      'mb-2 grid grid-cols-[60px_minmax(240px,420px)_180px_1fr_32px_32px] items-center gap-x-4 border-b border-slate-100 px-0 py-3 text-[13px] text-slate-700 hover:bg-slate-50 last:mb-0'
                    )}
                  >
                    <span>{item.id}</span>
                    <span className={cn('truncate pr-4')}>
                      {item.description}
                    </span>
                    <span>{typeLabel(item.type)}</span>
                    <span />
                    <span className={cn('flex items-center')}>
                      {blocked && (
                        <LockIcon
                          className={cn(
                            'mt-0.5 mb-[-2px] h-6 w-6 text-[#96969A] [&_path]:fill-current'
                          )}
                        />
                      )}
                    </span>
                    <Button
                      asChild
                      variant="ghost"
                      size="icon"
                      className={cn(
                        'h-6 w-8 text-orange-500 hover:bg-orange-50 hover:text-orange-600'
                      )}
                    >
                      <Link
                        aria-label={`Editar justificativa ${item.description}`}
                        to={`/configuracoes/justificativas-estadia/${item.id}/editar`}
                      >
                        <EditIcon
                          className={cn(
                            'h-6 w-6 text-[#EE7C2C] [&_path]:fill-current'
                          )}
                        />
                      </Link>
                    </Button>
                  </div>
                );
              })}
              {!loading && items.length === 0 && (
                <div className={cn('py-10 text-center text-sm text-slate-500')}>
                  Nenhuma justificativa encontrada.
                </div>
              )}
            </div>
          </div>
          <Paginator onPageChange={setPage} pagination={pagination} />
        </section>
      </main>
    </Scaffold>
  );
};

const ViagensJustificationForm: React.FC<{ id?: string }> = ({ id }) => {
  const formRef = React.useRef<FormHandles>(null);
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [initialData, setInitialData] = React.useState<any>();
  const editing = Boolean(id);

  React.useEffect(() => {
    if (!id) return;
    api
      .get(`${endpoint}/${id}`)
      .then(({ data }) => {
        const item = data?.data || data;
        setInitialData({
          description: item.description,
          type: typeOptions.find((option) => option.value === item.type),
          blockedAt: isBlocked(item) ? '1' : '0',
        });
      })
      .catch((error) => notify('error', handleApiError(error).message));
  }, [id]);

  React.useEffect(() => {
    if (initialData) formRef.current?.setData(initialData);
  }, [initialData]);

  const submit = async (data: any) => {
    if (!data.description?.trim() || !data.type) {
      formRef.current?.setErrors({
        description: !data.description?.trim() ? 'Informe a descrição' : '',
        type: !data.type ? 'Selecione o tipo' : '',
      });
      return;
    }
    setLoading(true);
    try {
      const payload = {
        description: data.description.trim(),
        type: data.type,
        deletedAt: data.blockedAt === '1' ? new Date().toISOString() : null,
      };
      if (editing) await api.put(`${endpoint}/${id}`, payload);
      else await api.post(endpoint, payload);
      notify(
        'success',
        editing
          ? 'Justificativa atualizada com sucesso'
          : 'Justificativa criada com sucesso'
      );
      navigate('/configuracoes/justificativas-estadia');
    } catch (error) {
      notify('error', handleApiError(error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Scaffold>
      <main className={cn(pageContainerClass)}>
        <section className={cn(formPanelClass, 'rounded-lg')}>
          <FormPageHeader
            title={editing ? 'Editar justificativa' : 'Nova justificativa'}
            icon={<TruckIcon className={cn('h-6 w-6 text-slate-700')} />}
            actions={
              <Button
                asChild
                variant="default"
                size="sm"
                className={cn(
                  '!h-8 !rounded-md !bg-[#EE7C2C] !px-4 !py-0',
                  'gap-2 text-sm font-GilroyBold text-white hover:!bg-[#d96d24]'
                )}
              >
                <Link to="/configuracoes/justificativas-estadia">
                  <ArrowLeft className={cn('h-4 w-4')} />
                  Voltar
                </Link>
              </Button>
            }
          />
          <Form ref={formRef} onSubmit={submit}>
            <div
              className={cn(
                'mt-6 grid grid-cols-1 gap-5 lg:grid-cols-3 lg:items-start'
              )}
            >
              <Input name="description" label="Nome" className={cn('!h-12')} />
              <Select
                name="type"
                label="Tipo"
                placeholder="Selecione o tipo"
                options={typeOptions}
                styles={selectStyles}
              />
              <ToggleInput
                name="blockedAt"
                label="Bloqueada"
                defaultChecked={initialData?.blockedAt === '1'}
              />
            </div>
            <div className={cn('mt-8 flex flex-col-reverse gap-3 sm:flex-row')}>
              <Button
                asChild
                type="button"
                variant="default"
                className={cn(
                  '!h-12 !rounded-md !bg-[#E9E9E9] !px-12 !py-0',
                  'text-base font-GilroyBold text-[#2E2E36] hover:!bg-[#D1D1D1]'
                )}
              >
                <Link to="/configuracoes/justificativas-estadia">Voltar</Link>
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className={cn(
                  '!h-12 !rounded-md !bg-[#EE7C2C] !px-12 !py-0',
                  'text-base font-GilroyBold text-white hover:!bg-[#d96d24]'
                )}
              >
                {loading ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </Form>
        </section>
      </main>
    </Scaffold>
  );
};

export const ViagensJustificationCreationPage: React.FC = () => (
  <ViagensJustificationForm />
);
export const ViagensJustificationUpdatePage: React.FC = () => (
  <ViagensJustificationForm id={(useParams() as { id: string }).id} />
);
